import { Location } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { GroupBy } from 'src/app/core/_enums/groupby';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { Combustible } from 'src/app/core/_models/combustible';
import { EnvironmentalData } from 'src/app/core/_models/environmentalData';
import { Version } from 'src/app/core/_models/version';
import { AuthService } from 'src/app/core/_services/auth.service';
import { CombustibleService } from 'src/app/core/_services/combustible.service';
import { EnvironmentalDataService } from 'src/app/core/_services/environmentalDataService';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { UploadService } from 'src/app/core/_services/upload.service';
import { VersionService } from 'src/app/core/_services/versions.service';
import { ResourceListRefreshService } from 'src/app/core/_services/resource.list.refresh.service';
import { UtilsService } from 'src/app/core/_utils/utils.service';
import { UpdateResourceAction } from 'src/app/store/actions/resource.actions';
import { AppState } from 'src/app/store/app.state';
import { GlobalConstants } from 'src/constants/global.constants';
import { environment } from 'src/environments/environment';
import { DialogDuplicateCodeComponent } from '../shared/dialog-duplicate-code/dialog-duplicate-code.component';
import { DialogOverviewComponent } from '../shared/dialog-overview/dialog-overview.component';

@Component({
  selector: 'app-combustible',
  templateUrl: './combustible.component.html',
  styleUrls: ['./combustible.component.scss']
})
export class CombustibleComponent implements OnInit {
  state: any = {
    perimeterId: null
  };

  currentItem: Combustible = {};
  form!: FormGroup;
  favoriteId?: string;

  fileToUpload?: File;
  justificationFilePath: string;

  @ViewChild('fileInput', { static: false })
  fileInput!: ElementRef;

  versionId: string = '';
  dbVersions: Version[] = [];
  environmentalFactor?: number;
  environmentalData?: EnvironmentalData;

  constructor(
    private activeRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private combustibleService: CombustibleService,
    private dialog: MatDialog,
    private router: Router,
    private snackbarService: SnackbarService,
    private store: Store<AppState>,
    private location: Location,
    private utilsService: UtilsService,
    private versionService: VersionService,
    private environmentalDataService: EnvironmentalDataService,
    private uploadService: UploadService,
    private authService: AuthService,
    private spinnerService: SpinnerOverlayService,
    private resourceListRefreshService: ResourceListRefreshService,
    private translate: TranslateService
  ) {
    this.justificationFilePath = environment.baseUrl + 'Documents/Justification/';
  }

  ngOnInit(): void {
    this.form = this.formBuilder?.group({
      code: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(9)]],
      displayName: ['', Validators.required],
      environmentalFactor: ['', Validators.required],
      description: [''],
      fileName: [],
      fileDescription: [],
      versionId: []
    });

    this.activeRoute.params.subscribe((params) => {
      const id = params['id'];
      if (!id || id === '0') {
        this.initForm(this.form, {});
        return;
      }
      this.combustibleService.get(id).subscribe({
        next: (data) => this.initForm(this.form, data),
        error: () => this.initForm(this.form, {})
      });
    });
  }

  isFormDisabled() {
    if (!this.currentItem?.resourceId) return false;
    const perimeterId = this.getPerimeterId();
    const ownerId = this.currentItem?.ownerId;
    const isRuadeResource = !ownerId || ownerId === GlobalConstants.EMPTY_GUID;
    if (isRuadeResource) {
      return perimeterId !== GlobalConstants.EMPTY_GUID;
    }
    return perimeterId !== ownerId;
  }

  private getPerimeterId(): string {
    if (history.state.perimeterId) {
      this.state.perimeterId = history.state.perimeterId;
      return history.state.perimeterId;
    }

    if (this.authService.roleValue?.perimeterId) {
      this.state.perimeterId = this.authService.roleValue.perimeterId;
      return this.authService.roleValue.perimeterId;
    }

    return this.state.perimeterId || '';
  }

  //Initialiser le formulaire
  initForm(form: FormGroup, currentItem: Combustible) {
    this.currentItem = currentItem;
    if (this.currentItem && this.currentItem.resourceId) {
      this.utilsService.getFavoriteIdForResource(this.currentItem.resourceId).then((res) => {
        this.favoriteId = res ? res.userFavoriteId : '';
      });

      if (!currentItem.ruade) {
        form.reset();
        form.patchValue(currentItem);
        return;
      }

      this.versionService.getAll().subscribe((versions) => {
        this.dbVersions = versions;

        if (versions[0]) {
          this.versionId = versions[0].versionId!;
        }

        this.environmentalDataService.getIndustryFuelEnvironmentalData(currentItem.resourceId!, this.versionId).subscribe((environmentalData: EnvironmentalData) => {
          currentItem.environmentalData = environmentalData;
          this.environmentalFactor = environmentalData.environmentalFactor;
          currentItem.environmentalFactor = environmentalData.environmentalFactor?.toString();

          form.reset();
          this.form.patchValue({
            versionId: versions[0].versionId
          });
          form.patchValue(currentItem);
          this.checkVersion(environmentalData);
        });
      });
    } else {
      form.reset();
      form.patchValue(currentItem);
    }
  }

  get formCrls() {
    return this.form?.controls;
  }

  onVersionChange(value: string) {
    this.versionId = value;
    let vId = this.currentItem.ruade ? value : undefined;
    this.environmentalDataService.getIndustryFuelEnvironmentalData(this.currentItem?.resourceId!, vId).subscribe((environmentalData: EnvironmentalData) => {
      this.currentItem.environmentalData = environmentalData;
      this.form.patchValue(this.currentItem);
      this.environmentalFactor = environmentalData.environmentalFactor;

      this.form.patchValue({
        environmentalFactor: environmentalData.environmentalFactor
      });

      this.checkVersion(environmentalData);
    });
  }

  checkVersion(environmentalData: EnvironmentalData) {
    if (environmentalData.environmentalDataId === GlobalConstants.EMPTY_GUID) {
      this.snackbarService.showMessage(this.translate.instant('error_resource_not_in_version'));
    }
  }

  // removeValidators(form: any) {
  //   for (const key in form.controls) {
  //       form.get(key).clearValidators();
  //       form.get(key).updateValueAndValidity();
  //   }
  // }

  clickduplicate(resourceId: string) {
    this.dialog
      .open(DialogDuplicateCodeComponent, { data: { code: this.currentItem.code, maxLength: 9, duplicate: (code: string) => this.combustibleService.duplicate(resourceId, code) } })
      .afterClosed()
      .subscribe((res) => {
        if (res !== undefined) this.majStore(SnackbarTypes.duplicate);
      });
  }

  clickDelete(resourceId: string) {
    const dialogRef = this.dialog.open(DialogOverviewComponent, {
      data: { title: this.translate.instant('confirm_delete_combustible') }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteItem(resourceId);
      }
    });
  }
  //Supprimer un combustible
  deleteItem(resourceId: string) {
    if (resourceId) {
      this.combustibleService.delete(resourceId).subscribe({
        next: (data) => {
          this.majStore(SnackbarTypes.delete);
          this.router.navigate([SiteMapUrl.MenuCombustible]);
        },
        error: (error) => {
          this.snackbarService.open(SnackbarTypes.error);
          console.error(error);
        }
      });
    }
  }

  majStore(snackbarMsgType: string) {
    this.combustibleService.getAll(GroupBy.family).subscribe(
      (arr) => {
        if (arr) {
          this.store.dispatch(new UpdateResourceAction(arr));
          this.snackbarService.open(snackbarMsgType);
        }
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
      }
    );

    this.resourceListRefreshService.triggerRefresh();
  }

  //Cliquer sur le bouton enregistrer
  async clickSave(currentItem: Combustible) {
    if (this.form.invalid) {
      return;
    }

    currentItem.code = this.form.get('code')?.value;
    currentItem.displayName = this.form.get('displayName')?.value;
    const envFactorValue = this.form.get('environmentalFactor')?.value;
    currentItem.environmentalFactor = envFactorValue;
    const envFactorNum = envFactorValue !== '' && envFactorValue != null ? Number(envFactorValue) : undefined;
    currentItem.environmentalData = {
      ...(currentItem.environmentalData ?? { resourceId: currentItem.resourceId ?? '' }),
      environmentalFactor: envFactorNum
    };
    currentItem.description = this.form.get('description')?.value;

    currentItem.fileDescription = this.form.get('fileDescription')?.value;
    currentItem.fileName = this.form.get('fileName')?.value;
    if (!currentItem.ruade) delete (currentItem as any).versionId;
    if (!currentItem.ruade) delete currentItem.environmentalData?.versionId;

    // INSERT
    if (!this.currentItem?.resourceId) {
      delete currentItem.resourceId;
      let rep = this.combustibleService.add(currentItem);
      rep.subscribe(
        (data: Combustible) => {
          this.currentItem = data;
          this.majStore(SnackbarTypes.add);
          this.router.navigate([SiteMapUrl.MenuCombustible, this.currentItem.resourceId], { state: { currentItem: this.currentItem, perimeterId: this.getPerimeterId() } });
        },
        (error) => {
          this.snackbarService.openError(error);
        }
      );
    }
    // UPDATE
    else {
      let rep = this.combustibleService.update(currentItem);
      rep.subscribe(
        (data: Combustible) => {
          this.currentItem = data;
          this.majStore(SnackbarTypes.update);
          this.location.replaceState(`${SiteMapUrl.MenuCombustible}/${data.resourceId}`);
        },
        (error) => {
          this.snackbarService.openError(error);
        }
      );
    }

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  //Sauvgarder ou modifier les données dans la base de données
  onUpdateOrSaveAch(currentItem: Combustible) {
    return this.currentItem?.resourceId ? Promise.resolve(this.combustibleService.update(currentItem)) : Promise.resolve(this.combustibleService.add(currentItem));
  }

  addToFavorite(resourceId: string) {
    this.utilsService.addToFavorite(resourceId).then((res) => {
      this.favoriteId = res;
    });
  }

  removeFromFavorite() {
    if (this.favoriteId) {
      this.utilsService.removeFromFavorite(this.favoriteId).then((res) => {
        this.favoriteId = '';
      });
    }
  }

  uploadProof(resourceId?: string, files?: any) {
    if (!resourceId || !files || files.length === 0) {
      return;
    }

    this.fileToUpload = <File>files[0];
    const formData = new FormData();
    formData.append('file', this.fileToUpload, `${resourceId}.${this.fileToUpload.name.split('.').pop()}`);

    this.spinnerService.show();
    this.uploadService.uploadProof(resourceId, formData).subscribe(
      (event: any) => {
        if (event.type === HttpEventType.Response) {
          this.spinnerService.hide();
          this.form.get('fileDescription')?.setValue(event?.body);
          this.form.get('fileName')?.setValue(this.fileToUpload?.name);
          this.fileInput.nativeElement.value = '';
        }
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      }
    );
  }

  deleteFile() {
    this.fileInput.nativeElement.value = '';
    this.form.get('fileName')?.reset();
    this.form.get('fileDescription')?.reset();
  }
}
